/**
 * TanStack Query hooks for Workflows.
 *
 * Covers:
 * - Workflow CRUD queries/mutations
 * - Workflow run queries (with polling for active runs)
 * - Run trigger and cancel mutations
 * - Agent/Skill list queries for step dropdowns
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  workflowApi,
  type ListWorkflowsParams,
  type CreateWorkflowInput,
  type UpdateWorkflowInput,
  type ListWorkflowRunsParams,
  type WorkflowRunStatus,
  type WorkflowScope,
} from '@/services/workflowApi';

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const workflowKeys = {
  all: ['workflows'] as const,
  list: (params?: ListWorkflowsParams) => [...workflowKeys.all, 'list', params] as const,
  detail: (id: string) => [...workflowKeys.all, 'detail', id] as const,
  runs: (workflowId: string, params?: ListWorkflowRunsParams) =>
    [...workflowKeys.all, 'runs', workflowId, params] as const,
  runDetail: (runId: string) => [...workflowKeys.all, 'run', runId] as const,
  agents: ['agents', 'active'] as const,
  skills: ['skills', 'active'] as const,
};

// ---------------------------------------------------------------------------
// Workflow Queries
// ---------------------------------------------------------------------------

/** Fetch workflows list */
export function useWorkflows(params?: ListWorkflowsParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: workflowKeys.list(params),
    queryFn: () => workflowApi.list(params),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

/** Fetch a single workflow by ID */
export function useWorkflow(id: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.detail(id ?? ''),
    queryFn: () => workflowApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Workflow Mutations
// ---------------------------------------------------------------------------

/** Create a new workflow */
export function useCreateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateWorkflowInput) => workflowApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    },
  });
}

/** Update an existing workflow */
export function useUpdateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateWorkflowInput }) =>
      workflowApi.update(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    },
  });
}

/** Delete a workflow */
export function useDeleteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workflowApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    },
  });
}

/** Activate workflow for current user */
export function useActivateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workflowApi.activate(id),
    // Returned so mutateAsync resolves after the list refetch: the toggle keeps its new state
    // instead of flickering back to the stale is_activated_by_current_user.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workflowKeys.all }),
  });
}

/** Deactivate workflow for current user */
export function useDeactivateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workflowApi.deactivate(id),
    // Returned so mutateAsync resolves after the list refetch: the toggle keeps its new state
    // instead of flickering back to the stale is_activated_by_current_user.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workflowKeys.all }),
  });
}

/** Duplicate a workflow */
export function useDuplicateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input?: { new_name?: string; new_slug?: string; scope?: WorkflowScope } }) =>
      workflowApi.duplicate(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Workflow Run Queries
// ---------------------------------------------------------------------------

/** Fetch run history for a workflow */
export function useWorkflowRuns(workflowId: string | undefined, params?: ListWorkflowRunsParams) {
  return useQuery({
    queryKey: workflowKeys.runs(workflowId ?? '', params),
    queryFn: () => workflowApi.listRuns(workflowId!, params),
    enabled: !!workflowId,
    staleTime: 10_000,
  });
}

/** Fetch a single run with step logs (polls while running/queued) */
export function useWorkflowRun(runId: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.runDetail(runId ?? ''),
    queryFn: () => workflowApi.getRun(runId!),
    enabled: !!runId,
    staleTime: 2_000,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'running' || status === 'queued') return 2_000;
      return false;
    },
  });
}

// ---------------------------------------------------------------------------
// Run Mutations
// ---------------------------------------------------------------------------

/** Trigger a workflow run */
export function useRunWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, input }: { workflowId: string; input?: Record<string, unknown> }) =>
      workflowApi.run(workflowId, input),
    onSuccess: (_data, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.runs(workflowId) });
    },
  });
}

/** Cancel a running workflow */
export function useCancelWorkflowRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (runId: string) => workflowApi.cancelRun(runId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.runDetail(data.id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.runs(data.workflow_id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Agent / Skill List Queries (for step dropdowns)
// ---------------------------------------------------------------------------

/** Fetch active agents for step dropdown */
export function useActiveAgents() {
  return useQuery({
    queryKey: workflowKeys.agents,
    queryFn: () => workflowApi.listAgents(),
    staleTime: 60_000,
    select: (data) => data.data,
  });
}

/** Fetch active skills for step dropdown */
export function useActiveSkills() {
  return useQuery({
    queryKey: workflowKeys.skills,
    queryFn: () => workflowApi.listSkills(),
    staleTime: 60_000,
    select: (data) => data.data,
  });
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const RUN_STATUS_LABELS: Record<WorkflowRunStatus, string> = {
  queued: 'Na fila',
  running: 'Executando',
  completed: 'Concluido',
  failed: 'Falhou',
  cancelled: 'Cancelado',
};

const RUN_STATUS_VARIANT: Record<WorkflowRunStatus, 'info' | 'warning' | 'success' | 'error' | 'neutral'> = {
  queued: 'info',
  running: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'neutral',
};

export function getRunStatusLabel(status: WorkflowRunStatus): string {
  return RUN_STATUS_LABELS[status] ?? status;
}

export function getRunStatusVariant(status: WorkflowRunStatus) {
  return RUN_STATUS_VARIANT[status] ?? 'neutral';
}
